"use client";

import { useToast } from "@/component/Toast/useToast";
import { copyToClipboard } from "@/util/clipboard";
import { useCallback, useMemo } from "react";

const ABSOLUTE_URL_REGEX = /^https?:\/\//i;

const ensureLeadingSlash = (value: string) =>
  value.startsWith("/") ? value : `/${value}`;

const DEFAULT_SHARE_TEXT = "Just made a cool video with VidMuse. Check it out!";

const COPY_ERROR_MESSAGE = "Failed to copy link";

interface UseShareLinkOptions {
  shareLink: string;
  shareText?: string;
}

const buildAbsoluteShareLink = (shareLink: string) => {
  if (!shareLink) {
    return "";
  }

  if (ABSOLUTE_URL_REGEX.test(shareLink)) {
    return shareLink;
  }

  const origin = globalThis.location?.origin ?? "";
  const normalizedPath = ensureLeadingSlash(shareLink);

  return origin ? `${origin}${normalizedPath}` : normalizedPath;
};

const openShareWindow = (url: string) => {
  if (!url) return;

  const openFn = globalThis.open;

  if (typeof openFn === "function") {
    openFn(url, "_blank", "noopener,noreferrer");
  }
};

export const useShareLink = (options: UseShareLinkOptions) => {
  const { shareLink, shareText = DEFAULT_SHARE_TEXT } = options;
  const { success: showSuccessToast, error: showErrorToast } = useToast();

  const resolvedShareLink = useMemo(
    () => buildAbsoluteShareLink(shareLink),
    [shareLink]
  );

  const encodedShareLink = useMemo(
    () => encodeURIComponent(resolvedShareLink),
    [resolvedShareLink]
  );

  const encodedShareText = useMemo(
    () => encodeURIComponent(shareText),
    [shareText]
  );

  const shareOnTwitter = useCallback(() => {
    openShareWindow(
      `https://x.com/intent/tweet?url=${encodedShareLink}&text=${encodedShareText}`
    );
  }, [encodedShareLink, encodedShareText]);

  const shareOnFacebook = useCallback(() => {
    openShareWindow(
      `https://www.facebook.com/sharer/sharer.php?u=${encodedShareLink}`
    );
  }, [encodedShareLink]);

  const shareOnReddit = useCallback(() => {
    openShareWindow(
      `https://www.reddit.com/submit?url=${encodedShareLink}&title=${encodedShareText}`
    );
  }, [encodedShareLink, encodedShareText]);

  const shareOnLinkedin = useCallback(() => {
    openShareWindow(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareLink}`
    );
  }, [encodedShareLink]);

  const handleCopyLink = useCallback(() => {
    if (!resolvedShareLink) {
      showErrorToast(COPY_ERROR_MESSAGE);
      return false;
    }

    try {
      return copyToClipboard(resolvedShareLink);
    } catch (error) {
      console.error("Failed to copy link", error);
      showErrorToast(COPY_ERROR_MESSAGE);
      return false;
    }
  }, [resolvedShareLink, showErrorToast]);

  return {
    shareLink: resolvedShareLink,
    shareText,
    shareOnTwitter,
    shareOnFacebook,
    shareOnReddit,
    shareOnLinkedin,
    handleCopyLink,
  };
};

export type UseShareLinkReturn = ReturnType<typeof useShareLink>;
